import {
  ajouterJours,
  ajouterMois,
  ecartJours,
  formaterJourLong,
  formaterMontant,
  jourAParis,
} from '@admin-pmvb/core'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type Echeance, requeteMoi, requetes } from '../api'
import { ActionsRapides } from '../composants/ActionsRapides'
import { Icone } from '../composants/Icone'
import { LogoConnu } from '../composants/Logo'
import { LigneTache } from '../composants/LigneTache'
import { Vide } from '../composants/Section'
import { EntetePage, TitreCarte } from '../composants/Ui'
import { jourCourt, jourMois, signe } from '../libelles'
import { refusInstallationValable } from '../pwa/plateforme'
import { BanniereInstallation } from './Installer'

/** « J-12 », « aujourd'hui », ou « en cours » pour une fenêtre ouverte. */
function compteARebours(date: string, finFenetre: string | null, aujourdhui: string): string {
  const n = ecartJours(aujourdhui, date)
  if (n > 0) return `J-${n}`
  if (n === 0) return 'aujourd’hui'
  if (finFenetre && finFenetre >= aujourdhui)
    return `en cours, J-${ecartJours(aujourdhui, finFenetre)}`
  return `dépassée de ${-n} j`
}

function BadgeEcheance({ e }: { e: Echeance }) {
  if (e.certitude === 'a_confirmer')
    return (
      <span className="badge grand ton-pointille">
        <Icone nom="help" />À confirmer
      </span>
    )
  if (e.statut === 'en_preparation')
    return (
      <span className="badge grand ton-ambre">
        <Icone nom="edit_note" />
        En préparation
      </span>
    )
  return (
    <span className="badge grand ton-ok">
      <Icone nom="schedule" />À venir
    </span>
  )
}

export function Accueil() {
  const { data: moi } = useSuspenseQuery(requeteMoi)
  const prenom = moi.nom.split(' ')[0]
  const aujourdhui = jourAParis(new Date())
  const echeances = useQuery(
    requetes.echeances({ du: ajouterMois(aujourdhui, -1), au: ajouterMois(aujourdhui, 12) }),
  )
  const aPayer = useQuery(requetes.paiements({ vue: 'a_payer' }))
  const aEncaisser = useQuery(requetes.paiements({ vue: 'a_encaisser' }))
  const decisions = useQuery(requetes.decisions)
  const semaine = useQuery(requetes.taches({ vue: 'ma_semaine' }))
  const mesTaches = (semaine.data ?? []).filter((t) => t.type !== 'decision')
  const horizon = ajouterJours(aujourdhui, 30)
  const sur30Jours = [...(aPayer.data ?? []), ...(aEncaisser.data ?? [])]
    .filter((p) => p.datePrevue && p.datePrevue <= horizon)
    .sort((a, b) => (a.datePrevue ?? '').localeCompare(b.datePrevue ?? ''))
  const total = (sens: 'payer' | 'encaisser') =>
    sur30Jours.filter((p) => p.sens === sens).reduce((s, p) => s + p.resteCts, 0)
  const prochaines = (echeances.data ?? [])
    .filter((e) => e.statut === 'a_venir' || e.statut === 'en_preparation')
    .filter((e) => (e.finFenetre ?? e.date) >= aujourdhui)
    .slice(0, 4)
  const jourLong = formaterJourLong(aujourdhui)

  return (
    <main>
      <EntetePage
        surtitre={jourLong.charAt(0).toUpperCase() + jourLong.slice(1)}
        titre={`Bonjour ${prenom}`}
        action={{ icone: 'upload_file', libelle: 'Déposer une pièce', lien: '/pieces' }}
      />
      <div className="pile-large">
        <ActionsRapides />
        <BanniereInstallation refusee={refusInstallationValable()} />
        <section className="pile" style={{ gap: 14 }}>
          <TitreCarte
            icone="event_upcoming"
            titre="Prochaines échéances"
            lien={{ to: '/echeancier', libelle: 'Tout l’échéancier', fleche: true }}
          />
          {echeances.isPending && <Vide>Chargement…</Vide>}
          {echeances.data && prochaines.length === 0 && <Vide>Aucune échéance à venir.</Vide>}
          <div className="grille">
            {prochaines.map((e) => (
              <Link
                key={e.id}
                to="/echeances/$id"
                params={{ id: e.id }}
                className="carte carte-lien carte-echeance"
              >
                <div className="carte-echeance-haut">
                  <span className="rebours">
                    {compteARebours(e.date, e.finFenetre, aujourdhui)}
                  </span>
                  <BadgeEcheance e={e} />
                </div>
                <strong>{e.libelle}</strong>
                <div className="meta-icones">
                  <span>
                    <Icone nom="domain" />
                    {e.entite}
                  </span>
                  <span>
                    <Icone nom="calendar_today" />
                    {e.finFenetre && e.finFenetre > e.date
                      ? `jusqu’au ${jourCourt(e.finFenetre)}`
                      : jourCourt(e.date)}
                  </span>
                </div>
                <div className="carte-echeance-montant">
                  {e.montantPrevuCts !== null && formaterMontant(e.montantPrevuCts, e.devise)}
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="deux-colonnes">
          <section className="carte carte-liste">
            <TitreCarte
              icone="task_alt"
              titre="Ma semaine"
              suffixe={semaine.data && <span className="mono">{mesTaches.length}</span>}
              lien={{ to: '/taches', libelle: 'Toutes les tâches', fleche: true }}
            />
            {semaine.isPending && <Vide>Chargement…</Vide>}
            {semaine.data && mesTaches.length === 0 && (
              <Vide>Rien qui vous revienne cette semaine.</Vide>
            )}
            {mesTaches.slice(0, 8).map((t) => (
              <LigneTache key={t.id} t={t} />
            ))}
            {mesTaches.length > 8 && (
              <p className="discret">
                Et {mesTaches.length - 8} autres : <Link to="/taches">voir tout</Link>.
              </p>
            )}
          </section>

          <div className="colonne">
            <section className="carte">
              <TitreCarte
                icone="payments"
                titre="30 prochains jours"
                lien={{ to: '/paiements', libelle: 'Paiements' }}
              />
              <div className="tuiles">
                <div className="tuile">
                  <span>
                    <Icone nom="north_east" />À payer
                  </span>
                  <span>{formaterMontant(total('payer'))}</span>
                </div>
                <div className="tuile entree">
                  <span>
                    <Icone nom="south_west" />À encaisser
                  </span>
                  <span>{formaterMontant(total('encaisser'))}</span>
                </div>
              </div>
              {sur30Jours.length === 0 && <Vide>Rien d’attendu.</Vide>}
              {sur30Jours.slice(0, 7).map((p) => (
                <div key={p.id} className="ligne-compacte">
                  <span className="date">{p.datePrevue && jourMois(p.datePrevue)}</span>
                  <span className="libelle">
                    <LogoConnu nom={p.tiers ?? p.libelle} taille={20} />
                    {p.libelle ?? p.echeance}
                    {p.retard === 'en_retard' && (
                      <span className="badge ton-alerte">En retard</span>
                    )}
                    {p.retard === 'releve_manquant' && (
                      <span className="badge ton-ambre">Relevé manquant</span>
                    )}
                  </span>
                  <span className={`montant ${p.sens === 'encaisser' ? 'entree' : ''}`}>
                    {signe(p.sens)}
                    {formaterMontant(p.resteCts, p.devise)}
                  </span>
                </div>
              ))}
            </section>

            <section className="carte">
              <TitreCarte
                icone="call_split"
                titre="Décisions à trancher"
                lien={{ to: '/releves', libelle: 'Relevés' }}
              />
              {decisions.data?.length === 0 && <Vide>Aucune.</Vide>}
              {decisions.data?.slice(0, 6).map((d) => (
                <Link
                  key={d.id}
                  to="/rapprochements/$id"
                  params={{ id: d.transactionId! }}
                  className="ligne-compacte sans-date"
                >
                  <span className="libelle mono">
                    <LogoConnu nom={d.libelle} taille={20} />
                    {d.libelle}
                  </span>
                  <span className="montant">{formaterMontant(d.montantCts, d.devise)}</span>
                  <Icone nom="chevron_right" />
                </Link>
              ))}
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}
